import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, articleLabel } from "../../lib/api";
import { PublicNav, PublicFooter } from "../../components/public/PublicNav";
import { AdSlot } from "../../components/public/AdSlot";
import { ReleasePosterCard } from "../../components/public/ReleasePosterCard";
import { RandomPicker } from "../../components/public/RandomPicker";
import { TrendingNow } from "../../components/public/TrendingNow";
import { NewsletterBand } from "../../components/public/NewsletterSignup";
import { useBulkVotes } from "../../hooks/useBulkVotes";
import { useSeo } from "../../hooks/useSeo";
import { ArrowRight, Clapperboard, CalendarClock, Sparkles } from "lucide-react";

export const PublicHome = () => {
  useSeo({
    title: "Rouge Première — L'agenda cinéma & séries",
    description: "Chaque semaine, la rédaction sélectionne les prochaines sorties cinéma et séries à découvrir en salle et en streaming (Netflix, Disney+, Prime Video, Apple TV+, Canal+, Max, Arte).",
  });
  const [films, setFilms] = useState([]);
  const [series, setSeries] = useState([]);
  const [releasedFilms, setReleasedFilms] = useState([]);
  const [releasedSeries, setReleasedSeries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/releases", { params: { kind: "Film", country: "France", limit: 8 } }),
      api.get("/releases", { params: { kind: "Série", limit: 8 } }),
      api.get("/releases", { params: { kind: "Film", released_only: true, sort: "fresh", limit: 8 } }),
      api.get("/releases", { params: { kind: "Série", released_only: true, sort: "fresh", limit: 8 } }),
      api.get("/articles", { params: { limit: 6 } }),
    ])
      .then(([f, s, rf, rs, a]) => {
        setFilms(f.data);
        setSeries(s.data);
        setReleasedFilms(rf.data);
        setReleasedSeries(rs.data);
        setArticles(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const strip = [...releasedFilms, ...releasedSeries];
  const { statsById, mineById } = useBulkVotes(strip);

  // Home top-of-fold layout is driven by two INDEPENDENT slots:
  //   1. "Édito" = always the latest article tagged "Édito" — never
  //      overwritten by any other type of publication.
  //   2. "À la une" = the article the editor manually pinned via the CMS
  //      (`is_featured=true`) — free to be any type (série, interview,
  //      vidéo…). Falls back gracefully when nothing is pinned.
  //   3. The "Dossiers & interviews" grid excludes both of the above to
  //      avoid the same article appearing twice on the home page.
  const hasEditoTag = (a) =>
    (a.tags || []).some((t) => (t || "").trim().toLowerCase() === "édito" || (t || "").trim().toLowerCase() === "edito");
  const latestEdito = articles.find(hasEditoTag) || null;
  // Multiple "À la une" pins are allowed. We show up to 3 on the home,
  // ordered by most-recently updated first so the editor can rotate a
  // pin by simply re-saving one of them. We always exclude the current
  // édito from this list to avoid a duplicate slot on the homepage.
  const featuredList = articles
    .filter((a) => a.is_featured && a.id !== latestEdito?.id)
    .sort((a, b) => {
      // Manual ordering wins (featured_order ASC, nulls last). Ties fall
      // back to updated_at DESC so newly pinned items stay near the top.
      const ao = Number.isFinite(a.featured_order) ? a.featured_order : 9999;
      const bo = Number.isFinite(b.featured_order) ? b.featured_order : 9999;
      if (ao !== bo) return ao - bo;
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    })
    .slice(0, 3);
  const excludeIds = new Set([latestEdito?.id, ...featuredList.map((a) => a.id)].filter(Boolean));
  const otherArticles = articles.filter((a) => !excludeIds.has(a.id));

  return (
    <div>
      <PublicNav />

      {/* ÉDITO compact — juste sous le menu */}
      {latestEdito && (
        <section className="max-w-[1300px] mx-auto px-5 md:px-10 pt-5 md:pt-6" data-testid="home-latest-edito">
          <Link
            to={`/articles/${latestEdito.slug}`}
            data-testid="home-latest-edito-link"
            className="group flex gap-4 rounded-lg overflow-hidden border border-[var(--line)] bg-white p-3 card-lift md:max-w-[760px]"
          >
            <div className="shrink-0 w-[84px] h-[84px] md:w-[110px] md:h-[110px] bg-neutral-50 flex items-center justify-center overflow-hidden rounded">
              {latestEdito.cover_image_url ? (
                <img src={latestEdito.cover_image_url} alt={latestEdito.title} className="w-full h-full object-cover" />
              ) : (
                <div className="grid place-items-center h-full w-full text-[var(--red)] font-display italic text-xs">Rouge Première</div>
              )}
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)] flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Le dernier édito
              </div>
              <h2 className="font-display font-semibold text-[24px] md:text-[28px] mt-1 leading-[1.15] md:leading-tight group-hover:underline decoration-[var(--red)] underline-offset-4 line-clamp-2" data-testid="home-latest-edito-title">
                {latestEdito.title}
              </h2>
              {latestEdito.subtitle && (
                <p className="mt-1 text-[17px] leading-[1.2] text-neutral-900 line-clamp-2" data-testid="home-latest-edito-subtitle">{latestEdito.subtitle}</p>
              )}
              <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[var(--red)]">
                Lire l'édito <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        </section>
      )}

      {/* HERO */}
      <section className="hero-vignette border-b border-[var(--line)]">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-neutral-500">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--red)]" />
            L'agenda cinéma & séries
            <span className="hidden md:inline text-neutral-300 mx-2">·</span>
            <span className="hidden md:inline">60 jours à venir</span>
          </div>
          <h1 className="mt-5 font-display text-[44px] leading-[1.05] md:text-[76px] md:leading-[0.98] font-semibold max-w-[900px]">
            Le rideau se lève sur les
            <span className="text-red-brand italic"> prochaines sorties</span>.
          </h1>
          <p className="mt-6 text-neutral-600 max-w-2xl md:text-lg leading-relaxed">
            Chaque semaine, la rédaction sélectionne les films cinéma et séries à découvrir sur
            Netflix, Disney+, Prime Video, Apple&nbsp;TV+, Canal+, Max et Arte, et donnera ses
            coups de cœur sous forme d'article à retrouver sur le site.
          </p>
          <div className="mt-8 flex flex-wrap items-start gap-x-3 gap-y-4">
            <Link to="/sorties" data-testid="hero-cta-sorties" className="btn-red">
              Voir toutes les sorties <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/articles" data-testid="hero-cta-articles" className="btn-ghost">
              Lire les dossiers
            </Link>
            <div className="flex flex-col gap-1">
              <RandomPicker />
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 pl-5">
                un film ou une série, au hasard
              </span>
            </div>
          </div>
        </div>
      </section>

      <NewsletterBand placement="home" />

      {/* AD SLOT TOP */}
      <AdSlot slot="home-top" className="max-w-[1300px] mx-auto px-5 md:px-10 mt-10" />

      {/* À LA UNE */}
      {featuredList.length > 0 && (
        <section
          className="max-w-[1300px] mx-auto px-5 md:px-10 mt-14"
          data-testid="home-featured-row"
        >
          <div className="grid gap-6 md:gap-8 md:grid-cols-1">
            {featuredList.length > 0 && (
              <div data-testid="home-featured-pin">
                <div className="mb-5">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" /> À la une
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl mt-1">Choix de la rédac</h2>
                </div>
                <div className="flex flex-col gap-3">
                  {featuredList.map((f) => (
                    <Link
                      key={f.id}
                      to={`/articles/${f.slug}`}
                      data-testid={`home-featured-pin-link-${f.slug}`}
                      className="group flex gap-4 rounded-lg overflow-hidden border border-[var(--line)] bg-white p-3 card-lift"
                    >
                      <div className="shrink-0 w-[110px] h-[110px] bg-neutral-50 flex items-center justify-center overflow-hidden rounded">
                        {f.cover_image_url ? (
                          <img
                            src={f.cover_image_url}
                            alt={f.title}
                            className="w-auto max-w-full max-h-[110px] object-contain"
                          />
                        ) : (
                          <div className="grid place-items-center h-full w-full text-[var(--red)] font-display italic text-xs">
                            Rouge Première
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)]">
                          {articleLabel(f)}
                        </div>
                        <h3 className="font-display text-base md:text-lg mt-1 leading-tight group-hover:underline decoration-[var(--red)] underline-offset-4 line-clamp-2">
                          {f.title}
                        </h3>
                        <span className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[var(--red)]">
                          {articleLabel(f)} <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* TENDANCES — Top 10 TMDB (films/séries, 24h/7j) */}
      <TrendingNow />

      {/* CINÉMA À L'AFFICHE (à venir, pas de vote) */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
              Cinéma
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">Cinéma à l'affiche en salle</h2>
          </div>
          <Link to="/sorties?kind=Film" className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]">
            Tout voir →
          </Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : films.length === 0 ? (
          <EmptyState message="Aucune sortie cinéma détectée pour l'instant." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {films.map((r) => (
              <ReleasePosterCard key={`up-film-${r.type}-${r.tmdb_id}`} r={r} hideVotes />
            ))}
          </div>
        )}
      </section>

      {/* SÉRIES À VENIR (streaming, pas de vote) */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
              Séries
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">Sur les écrans en streaming</h2>
          </div>
          <Link to="/sorties?kind=Série" className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]">
            Tout voir →
          </Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : series.length === 0 ? (
          <EmptyState message="Aucune sortie série détectée pour l'instant." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {series.map((r) => (
              <ReleasePosterCard key={`up-serie-${r.type}-${r.tmdb_id}`} r={r} hideVotes />
            ))}
          </div>
        )}
      </section>

      {/* DÉJÀ DANS LES SALLES (films sortis, vote actif) */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
              Vos réactions
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">Déjà dans les salles</h2>
          </div>
          <Link to="/sorties?kind=Film" className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]">
            Tout voir →
          </Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : releasedFilms.length === 0 ? (
          <EmptyState message="Aucun film sorti récemment détecté." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {releasedFilms.map((r) => (
              <ReleasePosterCard key={`rel-film-${r.type}-${r.tmdb_id}`} r={r} statsById={statsById} mineById={mineById} />
            ))}
          </div>
        )}
      </section>

      {/* DÉJÀ SUR VOS ÉCRANS (séries sorties, vote actif) */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
              Vos réactions
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">Déjà sur vos écrans</h2>
          </div>
          <Link to="/sorties?kind=Série" className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]">
            Tout voir →
          </Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : releasedSeries.length === 0 ? (
          <EmptyState message="Aucune série sortie récemment détectée." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {releasedSeries.map((r) => (
              <ReleasePosterCard key={`rel-serie-${r.type}-${r.tmdb_id}`} r={r} statsById={statsById} mineById={mineById} />
            ))}
          </div>
        )}
      </section>

      {/* AD MID-FEED */}
      <AdSlot slot="home-mid" className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16" />

      {/* ARTICLES */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
              La rédaction
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">Dossiers & interviews</h2>
          </div>
          <Link to="/articles" className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]">
            Tous les articles →
          </Link>
        </div>
        {otherArticles.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--line-strong)] bg-[var(--bg-cream)] p-10 md:p-14 text-center">
            <Clapperboard className="h-6 w-6 text-[var(--red)] mx-auto" />
            <div className="mt-3 font-display text-2xl">Les premiers dossiers et interviews arrivent bientôt.</div>
            <p className="text-sm text-neutral-500 mt-2">
              La rédaction prépare ses coups de cœur — repassez très prochainement.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherArticles.slice(0, 6).map((a) => <ArticleTeaser key={a.id} a={a} />)}
          </div>
        )}
      </section>

      {/* COMING SOON teaser */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-20 mb-8">
        <div className="rounded-lg border border-[var(--line)] bg-white p-8 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium flex items-center gap-2">
              <CalendarClock className="h-3.5 w-3.5" /> Bientôt
            </div>
            <h3 className="font-display text-3xl md:text-4xl mt-2">
              Des <span className="editorial-underline">avant-premières</span> et rencontres exclusives.
            </h3>
            <p className="text-neutral-600 mt-3 leading-relaxed max-w-xl">
              Avant-premières, festivals, rencontres avec les équipes : on vous prépare des
              rendez-vous exclusifs. Détails à venir.
            </p>
          </div>
          <div className="text-right">
            <div className="font-display text-6xl md:text-8xl text-[var(--red)] italic">
              soon
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

const ArticleTeaser = ({ a }) => (
  <Link
    to={`/articles/${a.slug}`}
    data-testid={`article-teaser-${a.slug}`}
    className="group block bg-white border border-[var(--line)] rounded-md overflow-hidden card-lift"
  >
    <div className="h-[200px] bg-neutral-50 flex items-center justify-center overflow-hidden">
      {a.cover_image_url ? (
        <img
          src={a.cover_image_url}
          alt={a.title}
          className="w-auto max-w-full max-h-[200px] object-contain transition-transform duration-700 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="grid place-items-center h-full text-[var(--red)] font-display text-xl italic">
          Rouge Première
        </div>
      )}
    </div>
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--red)]">
        {articleLabel(a)}
      </div>
      <div className="font-display text-xl mt-1 leading-tight group-hover:underline decoration-[var(--red)] underline-offset-4">
        {a.title}
      </div>
      {a.subtitle && (
        <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{a.subtitle}</p>
      )}
      <div className="text-[11px] text-neutral-400 mt-3">{fmtDate(a.published_at)}</div>
    </div>
  </Link>
);

const SkeletonRow = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="aspect-[2/3] rounded-md bg-neutral-100 animate-pulse" />
    ))}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="rounded-lg border border-dashed border-[var(--line-strong)] bg-[var(--bg-cream)] p-10 text-center text-sm text-neutral-500">
    {message}
  </div>
);
